import Feather from '@expo/vector-icons/Feather';
import { useNavigation } from '@react-navigation/native';
import { VisitAuthorizationStatusType, VisitsModel } from '@w3lcome/types';
import { fontFamily } from '_/config/theme';
import themes from '_/constants/themes';
import { useVisitsContext } from '_/hooks/VisitsProvider';
import { FormatedVisitsModel } from '_/interfaces/FormatedVisitsModel';
import { AppRoute, VisitorProps } from '_/routes/types';
import DenyConfirmationModal from '_/screens/Visitor/DenyConfirmationModal';
import { getDateLocale } from '_/helpers/getDateLocale';
import { format, formatDistanceToNow } from 'date-fns';
import * as Localization from 'expo-localization';
import moment from 'moment-timezone';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  View,
  TouchableOpacity,
  ImageBackground,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { showMessage } from 'react-native-flash-message';
import { useSelector } from 'react-redux';

import Text from '../Text';

interface VisitItemProps {
  visit: FormatedVisitsModel;
  isCredentialed?: boolean;
  getUpdatedItem?: (visit: VisitsModel) => void;
}

export function VisitItem({ visit, isCredentialed, getUpdatedItem }: VisitItemProps) {
  const navigation = useNavigation<VisitorProps>();
  const { allowDenyVisit, setSelectedVisit, allowDenyCredentialed } = useVisitsContext();
  const { t: translate, i18n } = useTranslation();
  const locale = getDateLocale(i18n.language);
  const { id: hostId, canAuthorizeAllCredentialing } = useSelector((state: any) => state?.host);

  const [authorizationLoading, setAuthorizationLoading] = useState(false);
  const [showVisitDenialModal, setShowVisitDenialModal] = useState(false);

  const isExpected = useMemo(() => {
    return !!visit.expectedAt && !visit.checkinAt;
  }, [visit]);

  const convertTime = useCallback((time: Date) => {
    return moment(time).tz(Localization.getCalendars()[0]?.timeZone || 'America/Sao_Paulo');
  }, []);

  const dateLine = useMemo(() => {
    if (!visit) return '';

    const tz = Localization.getCalendars()[0]?.timeZone || 'America/Sao_Paulo';
    const isDateToday = (d: Date | undefined) =>
      !!d && moment(d).tz(tz).isSame(moment().tz(tz), 'day');

    if (!isExpected && visit.checkinAt) {
      const at = convertTime(visit.checkinAt).toDate();
      return isDateToday(visit.checkinAt)
        ? formatDistanceToNow(at, { addSuffix: true, locale })
        : format(at, 'PPpp', { locale });
    }

    if (isExpected && visit.expectedAt) {
      const at = convertTime(visit.expectedAt).toDate();
      const main = isDateToday(visit.expectedAt)
        ? format(at, 'p', { locale })
        : format(at, 'PPpp', { locale });
      return visit?.meeting?.title ? `${main} - ${visit.meeting.title}` : main;
    }

    return '';
  }, [convertTime, isExpected, locale, visit]);

  if (!visit) return null;

  function handleOpen() {
    setSelectedVisit(visit);
    navigation.navigate(AppRoute.VISITOR);
  }

  function canHostAuthorize() {
    if (!isCredentialed || canAuthorizeAllCredentialing) return true;
    return visit.hostId === hostId;
  }

  async function handleVisitAccessAllowed() {
    try {
      setAuthorizationLoading(true);
      const status = VisitAuthorizationStatusType.ALLOWED;

      if (isCredentialed) {
        const updated = await allowDenyCredentialed(visit.id, status);
        getUpdatedItem?.(updated);
      } else {
        await allowDenyVisit({
          visit,
          status,
          isCanceled: false,
        });
      }

      showMessage({
        message: translate('visitSaga.allowAccessSuccess'),
        backgroundColor: themes.colors.success,
      });
    } catch {
      showMessage({
        message: translate('visitSaga.allowAccessError'),
        backgroundColor: themes.colors.danger,
      });
    }

    setAuthorizationLoading(false);
  }

  async function handleVisitAccessDenied(inputNotes?: string | null) {
    try {
      setAuthorizationLoading(true);
      const status = VisitAuthorizationStatusType.DENIED;
      const note = inputNotes?.trim() || null;

      if (isCredentialed) {
        const updated = await allowDenyCredentialed(visit.id, status, note);
        getUpdatedItem?.(updated);
      } else {
        await allowDenyVisit({
          visit,
          status,
          visitorNoteDenied: note,
          isCanceled: true,
        });
      }

      showMessage({
        message: translate('visitSaga.denyAccessSuccess'),
        backgroundColor: themes.colors.success,
      });
    } catch {
      showMessage({
        message: translate('visitSaga.denyAccessError'),
        backgroundColor: themes.colors.danger,
      });
    }

    setShowVisitDenialModal(false);
    setAuthorizationLoading(false);
  }

  function getInitials(fullname?: string | null) {
    if (!fullname) return 'X';
    const names = fullname.split(' ');
    let initials = names[0][0].toUpperCase();
    if (names.length > 1) {
      initials += names[names.length - 1][0].toUpperCase();
    }
    return initials;
  }

  function renderAvatar() {
    return (
      <View style={styles.avatarWrapper}>
        {visit.pictureThumbUrl ? (
          <ImageBackground
            source={{ uri: visit.pictureThumbUrl }}
            style={styles.avatar}
            imageStyle={{ borderRadius: 28 }}
          />
        ) : (
          <View style={styles.initialsAvatar}>
            <Text style={styles.initialsText}>{getInitials(visit.name)}</Text>
          </View>
        )}
      </View>
    );
  }

  function renderStatusBadge() {
    let bg = themes.colors.warning;
    let label = translate('pendingOne');

    if (visit.authorizationStatus === VisitAuthorizationStatusType.ALLOWED) {
      bg = themes.colors.success;
      label = translate('approvedOne');
    }

    if (visit.authorizationStatus === VisitAuthorizationStatusType.DENIED) {
      bg = themes.colors.danger;
      label = translate('deniedOne');
    }

    const checkinMap: Record<string, string> = {
      IPAD: translate('checkinTotem'),
      DASHBOARD: translate('checkinDashboard'),
      API: translate('checkinTouchless'),
      MOBILE: translate('checkinMobile'),
      INVITES: translate('checkinInvite'),
    };

    let checkinLabel: string | null = null;

    if (visit.checkinAt && visit.checkinType) {
      checkinLabel = checkinMap[visit.checkinType];
    } else if (isExpected) {
      checkinLabel = translate('checkinInvite');
    }

    return (
      <View style={styles.badgeContainer}>
        {visit.authorizationStatus !== null && (
          <View style={[styles.badgeInner, { backgroundColor: bg }]}>
            <Text style={styles.badgeText}>{label}</Text>
          </View>
        )}

        {checkinLabel && !visit.credentialedVisit && (
          <View style={styles.checkinBadge}>
            <Text style={styles.badgeText}>{checkinLabel}</Text>
          </View>
        )}
      </View>
    );
  }

  function renderActions() {
    if (authorizationLoading) {
      return <ActivityIndicator size="small" />;
    }

    if (visit.authorizationStatus === VisitAuthorizationStatusType.AWAITING && canHostAuthorize()) {
      return (
        <View style={styles.actions}>
          <TouchableOpacity
            style={[styles.actionBtn, { backgroundColor: themes.colors.danger }]}
            onPress={() => setShowVisitDenialModal(true)}
          >
            <Feather name="x" size={20} color="#fff" />
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionBtn, { backgroundColor: themes.colors.success }]}
            onPress={handleVisitAccessAllowed}
          >
            <Feather name="check" size={20} color="#fff" />
          </TouchableOpacity>
        </View>
      );
    }

    return <Feather name="chevron-right" size={18} color={themes.colors.graySecondary} />;
  }

  return (
    <>
      <TouchableOpacity style={styles.card} activeOpacity={0.9} onPress={handleOpen}>
        {renderAvatar()}

        <View style={styles.content}>
          <View style={styles.topRow}>
            <Text numberOfLines={2} style={styles.name}>
              {visit.name || translate('visitor.anonymous')}
            </Text>
          </View>

          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
            {isExpected && (
              <Feather
                name="clock"
                size={12}
                color={themes.colors.defaultBlue}
                style={{ marginRight: 4 }}
              />
            )}

            <Text style={styles.dateText}>
              {isExpected ? `${translate('scheduledOne')} • ${dateLine}` : dateLine}
            </Text>
          </View>
        </View>

        <View style={styles.right}>{renderActions()}</View>
        {renderStatusBadge()}
      </TouchableOpacity>

      {showVisitDenialModal && (
        <DenyConfirmationModal
          visible={showVisitDenialModal}
          hideModal={() => setShowVisitDenialModal(false)}
          denyVisit={handleVisitAccessDenied}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: themes.colors.white,
    marginBottom: 14,
    padding: 12,
    paddingBottom: 24,
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 5,
  },
  avatarWrapper: {
    marginRight: 14,
  },
  avatar: {
    width: 56,
    height: 56,
  },
  initialsAvatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: themes.colors.defaultBlue,
    alignItems: 'center',
    justifyContent: 'center',
  },
  initialsText: {
    color: themes.colors.white,
    fontSize: 20,
    fontFamily: fontFamily.semibold,
  },
  content: {
    flex: 1,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  name: {
    flex: 1,
    marginRight: 8,
    fontSize: 15,
    fontFamily: fontFamily.semibold,
    color: themes.colors.grayPrimary,
    flexWrap: 'wrap',
  },
  dateText: {
    marginTop: 4,
    fontSize: 12.5,
    color: themes.colors.graySecondary,
    fontFamily: fontFamily.regular,
  },
  badgeText: {
    color: themes.colors.white,
    fontSize: 10,
    fontFamily: fontFamily.semibold,
  },
  badgeInner: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 18,
    paddingHorizontal: 24,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    borderBottomLeftRadius: 20,
  },
  badgeContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkinBadge: {
    marginLeft: 6,
    backgroundColor: themes.colors.defaultBlue,
    paddingHorizontal: 8,
    height: 18,
    justifyContent: 'center',
    borderRadius: 4,
  },
  right: {
    marginLeft: 10,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 8,
    marginLeft: 6,
  },
});
